import type { ReactNode } from "react";
import {
  Archive,
  BookOpen,
  CheckCircle,
  Clock,
  House,
  Microphone,
  Notebook,
  PencilSimpleLine,
  SealWarning,
  SquaresFour,
} from "@phosphor-icons/react";
import { Link, useLocation } from "react-router-dom";

export type CoverTone = "mountain" | "linen" | "night" | "botanical" | "sea" | "city";

const coverToneClasses: Record<CoverTone, string> = {
  mountain: "bg-[linear-gradient(180deg,var(--as-paper-raised),var(--as-paper-2)_56%,var(--as-walnut-soft)_100%)] text-ink-1",
  linen: "bg-[radial-gradient(circle_at_50%_20%,var(--as-paper-raised),var(--as-paper)_64%,var(--as-paper-3))] text-ink-1",
  night: "bg-[linear-gradient(145deg,var(--as-walnut-deep),var(--as-ink)_70%,var(--as-walnut-soft))] text-[var(--as-paper-raised)]",
  botanical: "bg-[linear-gradient(180deg,var(--as-paper-raised),var(--as-verify-tint)_58%,var(--as-verify))] text-ink-1",
  sea: "bg-[linear-gradient(180deg,var(--as-proof),var(--as-ink))] text-[var(--as-paper-raised)]",
  city: "bg-[linear-gradient(180deg,var(--as-paper-raised),var(--as-proof-tint)_48%,var(--as-proof-soft))] text-ink-1",
};

export function PressLogo({ size = "md", stacked = false, inverse = false }: {
  size?: "sm" | "md" | "lg";
  stacked?: boolean;
  inverse?: boolean;
}) {
  const wordSize = size === "lg" ? "text-[1.85rem] md:text-[2.35rem]" : size === "sm" ? "text-[1.05rem]" : "text-[1.45rem]";
  const markSize = size === "lg" ? "h-11 w-10" : size === "sm" ? "h-7 w-6" : "h-9 w-8";
  const ink = inverse ? "text-[var(--as-paper-raised)]" : "text-ink-1";
  return (
    <div className={`flex items-center ${stacked ? "flex-col gap-2 text-center" : "gap-4"}`}>
      <LogoMark className={`${markSize} ${inverse ? "text-[var(--as-walnut-soft)]" : "text-walnut"}`} />
      <div>
        <div className={`font-display font-semibold leading-none tracking-as-normal ${wordSize} ${ink}`}>
          PullPress
        </div>
        <div className={`mt-1 font-ui text-[12px] font-medium ${inverse ? "text-[var(--as-walnut-tint)]" : "text-ink-3"}`}>
          Books are finished here.
        </div>
      </div>
    </div>
  );
}

export function LogoMark({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 58" className={className} aria-hidden="true">
      <path
        d="M4.5 7.5 24 3l19.5 4.5v22.4c0 11.4-7.5 19.6-19.5 24.6C12 49.5 4.5 41.3 4.5 29.9V7.5Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      />
      <text
        x="24"
        y="35"
        textAnchor="middle"
        fontFamily="Source Serif 4, Georgia, serif"
        fontSize="25"
        fill="currentColor"
      >
        P
      </text>
      <text
        x="30"
        y="42"
        textAnchor="middle"
        fontFamily="Source Serif 4, Georgia, serif"
        fontSize="17"
        fill="currentColor"
      >
        P
      </text>
    </svg>
  );
}

export function ShellPage({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div className={`mx-auto w-full max-w-[1360px] px-4 py-4 sm:px-6 lg:px-8 ${className}`}>
      {children}
    </div>
  );
}

export function PressPanel({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <section className={`min-w-0 rounded-as-3 border border-[color:color-mix(in_oklab,var(--as-rule)_48%,transparent)] bg-paper-raised/72 shadow-as-1 backdrop-blur-2xl ${className}`}>
      {children}
    </section>
  );
}

export function PressEyebrow({ children, dot = false, className = "" }: {
  children: ReactNode;
  dot?: boolean;
  className?: string;
}) {
  return (
    <div className={`flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.1em] text-ink-3 ${className}`}>
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-walnut" />}
      <span>{children}</span>
    </div>
  );
}

export function BookCover({
  title,
  subtitle,
  author,
  tone = "mountain",
  className = "",
}: {
  title: string;
  subtitle?: string;
  author?: string;
  tone?: CoverTone;
  className?: string;
}) {
  return (
    <div className={`relative aspect-[6/9] overflow-hidden rounded-as-2 border border-[color:color-mix(in_oklab,var(--as-paper-raised)_78%,transparent)] shadow-as-page ${coverToneClasses[tone]} ${className}`}>
      <div className="absolute inset-x-0 top-0 h-1/2 opacity-50">
        <svg viewBox="0 0 240 160" className="h-full w-full" preserveAspectRatio="none" aria-hidden="true">
          <path d="M-20 118 45 62l34 31 42-61 39 48 28-26 72 70" fill="none" stroke="currentColor" strokeOpacity=".25" strokeWidth="2" />
          <path d="M-12 132 62 87l30 26 47-45 34 35 39-22 42 45" fill="none" stroke="currentColor" strokeOpacity=".18" strokeWidth="2" />
        </svg>
      </div>
      {tone === "botanical" && (
        <svg viewBox="0 0 160 220" className="absolute inset-x-0 bottom-10 mx-auto h-32 w-28 text-walnut opacity-50" aria-hidden="true">
          <path d="M77 190C77 124 87 73 112 34" fill="none" stroke="currentColor" strokeWidth="2" />
          <path d="M89 100c-31 1-45-14-47-34 28-4 45 9 47 34Zm8 34c31-2 47-18 49-39-31-1-47 15-49 39Z" fill="none" stroke="currentColor" strokeWidth="2" />
        </svg>
      )}
      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(0,0,0,0.10),transparent_14%,transparent_86%,rgba(255,255,255,0.24))]" />
      <div className="relative flex h-full flex-col items-center justify-between p-4 text-center">
        <div className="font-mono text-[9px] uppercase tracking-[0.2em] opacity-70">PullPress</div>
        <div>
          <div className="font-display text-[clamp(1.05rem,2.2vw,1.75rem)] font-semibold leading-[1.08] tracking-as-normal">
            {title}
          </div>
          {subtitle && <div className="mx-auto mt-3 max-w-[10rem] font-text text-sm leading-snug opacity-75">{subtitle}</div>}
        </div>
        <div className="font-mono text-[9px] uppercase tracking-[0.2em] opacity-75">{author ?? "P. P. Author"}</div>
      </div>
    </div>
  );
}

export function BookStackScene({ className = "" }: { className?: string }) {
  const books = [
    "h-52 bg-[linear-gradient(90deg,var(--as-ink-2),var(--as-proof))]",
    "h-60 bg-[linear-gradient(90deg,var(--as-verify),var(--as-ink-2))]",
    "h-64 bg-[linear-gradient(90deg,var(--as-walnut-deep),var(--as-ink-2))]",
    "h-72 bg-[linear-gradient(90deg,var(--as-ink),var(--as-proof))]",
    "h-56 bg-[linear-gradient(90deg,var(--as-walnut),var(--as-walnut-deep))]",
  ];
  return (
    <div className={`relative min-h-[320px] overflow-hidden rounded-[14px] border border-rule bg-[radial-gradient(circle_at_70%_25%,var(--as-paper-raised),var(--as-paper)_52%,var(--as-paper-2))] ${className}`}>
      <svg className="absolute inset-0 h-full w-full text-rule-strong opacity-40" viewBox="0 0 720 420" aria-hidden="true">
        <path d="M90 245c86-52 164-76 237-71 94 6 159-26 240-94M22 306c97-55 203-77 318-64 103 12 203-24 306-107" fill="none" stroke="currentColor" strokeWidth="1" />
        <path d="M130 332 484 244l74 52-356 88-72-52Z" fill="none" stroke="currentColor" strokeWidth="1" />
      </svg>
      <div className="absolute bottom-10 right-[7%] flex items-end gap-2">
        {books.map((book, index) => (
          <div key={book} className={`relative w-14 rounded-[3px] border border-[rgba(255,253,248,0.24)] shadow-as-page ${book}`}>
            <div className="absolute inset-y-3 left-3 w-px bg-[rgba(255,253,248,0.25)]" />
            <div className="absolute left-1/2 top-8 -translate-x-1/2 font-display text-sm text-[var(--as-walnut-tint)] opacity-70">PP</div>
            <div className="absolute bottom-7 left-1/2 h-10 w-px -translate-x-1/2 bg-[rgba(255,253,248,0.24)]" />
            <span className="sr-only">Generated book spine {index + 1}</span>
          </div>
        ))}
      </div>
      <div className="absolute bottom-14 left-[13%] h-3 w-72 rotate-[-13deg] rounded-full bg-[rgba(18,16,14,0.15)] blur-md" />
      <div className="absolute bottom-[4.8rem] left-[15%] h-3 w-72 rotate-[-13deg] rounded-full border border-walnut-soft bg-[linear-gradient(90deg,var(--as-walnut-deep),var(--as-paper-raised),var(--as-walnut))]" />
      <div className="absolute bottom-[5.7rem] left-[28%] h-5 w-20 rotate-[-13deg] rounded-full border border-walnut-deep bg-[var(--as-ink-2)]" />
    </div>
  );
}

export function ManuscriptSpread({ className = "" }: { className?: string }) {
  return (
    <div className={`relative overflow-hidden rounded-as-2 border border-rule bg-paper-raised p-4 shadow-as-2 ${className}`}>
      <div className="grid grid-cols-2 gap-1">
        {["The Road Unfolds", "Not for a destination"].map((title) => (
          <div key={title} className="min-h-[220px] border border-rule-faint bg-[linear-gradient(180deg,var(--as-paper-raised),var(--as-paper))] px-6 py-8">
            <h4 className="font-display text-xl font-semibold leading-tight text-ink-1">{title}</h4>
            <div className="mt-5 space-y-2">
              {Array.from({ length: 8 }, (_, index) => (
                <div
                  key={index}
                  className="h-px bg-ink-4/40"
                  style={{ width: `${92 - (index % 3) * 12}%` }}
                />
              ))}
            </div>
            <div className="mt-8 h-28 rounded-sm bg-[linear-gradient(180deg,var(--as-proof-tint),var(--as-paper-2)_56%,var(--as-verify-tint))] opacity-70" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function Waveform({ bars = 96, active = true, className = "" }: {
  bars?: number;
  active?: boolean;
  className?: string;
}) {
  const heights = Array.from({ length: bars }, (_, index) => 18 + ((index * 17) % 58));
  return (
    <div className={`flex h-20 items-center gap-[3px] ${className}`} aria-label="Generated recording waveform">
      {heights.map((height, index) => (
        <div
          key={`${height}-${index}`}
          className={active ? "w-[3px] rounded-full bg-walnut-soft" : "w-[3px] rounded-full bg-rule-strong"}
          style={{ height }}
        />
      ))}
    </div>
  );
}

export function ReadinessRing({ value, label = "Ready" }: { value: number; label?: string }) {
  const normalized = Math.max(0, Math.min(100, value));
  return (
    <div className="relative grid h-28 w-28 place-items-center rounded-full" style={{ background: `conic-gradient(var(--as-verify) ${normalized * 3.6}deg, var(--as-walnut-soft) ${normalized * 3.6}deg 360deg)` }}>
      <div className="grid h-20 w-20 place-items-center rounded-full bg-paper-raised text-center">
        <div>
          <div className="font-display text-2xl font-semibold leading-none text-ink-1">{normalized}%</div>
          <div className="mt-1 font-mono text-[10px] uppercase tracking-as-eyebrow text-ink-3">{label}</div>
        </div>
      </div>
    </div>
  );
}

export function StatusPill({ children, tone = "draft" }: {
  children: ReactNode;
  tone?: "draft" | "progress" | "editing" | "ready" | "published" | "blocked";
}) {
  const toneClass = {
    draft: "bg-paper-2/80 text-ink-3 border-rule",
    progress: "bg-proof-tint text-proof border-proof-soft/30",
    editing: "bg-highlight text-walnut-deep border-walnut-soft/50",
    ready: "bg-verify-tint text-verify border-verify-soft/30",
    published: "bg-verify text-paper-raised border-verify",
    blocked: "bg-flag-tint text-flag border-flag/30",
  }[tone];
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.08em] ${toneClass}`}>
      {children}
    </span>
  );
}

export function MobilePreviewCard() {
  return (
    <div className="mx-auto w-full max-w-[270px] rounded-[30px] border border-rule-strong bg-paper-raised p-3 shadow-as-3">
      <div className="rounded-[24px] border border-rule bg-paper px-4 py-5">
        <div className="flex items-center justify-between">
          <span className="font-mono text-[10px] text-ink-2">9:41</span>
          <LogoMark className="h-7 w-6 text-walnut" />
        </div>
        <div className="mt-5">
          <PressEyebrow>PullPress</PressEyebrow>
          <div className="mt-4 flex items-center gap-3 rounded-as-3 border border-rule bg-paper-raised p-3">
            <BookCover title="The Long Road" tone="mountain" className="w-12 shrink-0" />
            <div>
              <div className="font-display text-lg font-semibold leading-tight">The Road I Traveled</div>
              <StatusPill tone="progress">In production</StatusPill>
            </div>
          </div>
          <div className="mt-5 space-y-2">
            <MobileActionRow label="Work the proof" icon={<PencilSimpleLine size={16} className="text-walnut" />} />
            <MobileActionRow label="Check source notes" icon={<Notebook size={16} className="text-walnut" />} />
            <MobileActionRow label="Ask the press" icon={<Archive size={16} className="text-walnut" />} />
          </div>
          <div className="mt-5 grid grid-cols-2 gap-3">
            <ReadinessMini value="62%" label="Ready" />
            <ReadinessMini value="24" label="Chapters" />
          </div>
        </div>
      </div>
    </div>
  );
}

function MobileActionRow({ label, icon }: { label: string; icon: ReactNode }) {
  return (
    <div className="flex items-center justify-between rounded-as-2 border border-rule bg-paper-raised p-3">
      <div className="flex items-center gap-2">
        {icon}
        <span className="font-ui text-sm text-ink-1">{label}</span>
      </div>
      <span className="text-ink-4">›</span>
    </div>
  );
}

function ReadinessMini({ value, label }: { value: string; label: string }) {
  return (
    <div className="rounded-as-2 border border-rule bg-paper-raised p-3 text-center">
      <div className="font-display text-xl font-semibold text-ink-1">{value}</div>
      <div className="font-mono text-[9px] uppercase tracking-as-eyebrow text-ink-4">{label}</div>
    </div>
  );
}

export function MobileBottomNav() {
  const location = useLocation();
  const links = [
    { to: "/dashboard", label: "Home", icon: House },
    { to: "/dashboard", label: "Books", icon: BookOpen },
    { to: "/new-project", label: "Start", icon: Microphone },
    { to: "/pricing", label: "Pricing", icon: SquaresFour },
    { to: "/support", label: "Help", icon: Notebook },
  ];
  return (
    <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-[color:color-mix(in_oklab,var(--as-paper-raised)_76%,transparent)] bg-paper-raised/90 px-2 py-2 shadow-as-2 backdrop-blur-2xl md:hidden" aria-label="Mobile author navigation">
      <div className="grid grid-cols-5">
        {links.map(({ to, label, icon: Icon }) => {
          const active = location.pathname === to;
          return (
            <Link key={`${to}-${label}`} to={to} className={`flex min-h-11 flex-col items-center justify-center gap-1 rounded-as-2 px-2 py-1.5 font-ui text-[11px] font-semibold ${active ? "bg-walnut-tint text-walnut-deep" : "text-ink-3"}`}>
              <Icon size={18} />
              <span>{label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

export function ChecklistRow({ label, status }: {
  label: string;
  status: "complete" | "warning" | "pending";
}) {
  const Icon = status === "complete" ? CheckCircle : status === "warning" ? SealWarning : Clock;
  const color = status === "complete" ? "text-verify" : status === "warning" ? "text-flag" : "text-ink-4";
  return (
    <div className="flex items-center justify-between gap-4 border-b border-rule-faint py-2 last:border-0">
      <div className="flex items-center gap-2">
        <Icon size={17} weight="regular" className={color} />
        <span className="font-ui text-sm text-ink-2">{label}</span>
      </div>
      <span className={`font-mono text-[10px] uppercase tracking-as-eyebrow ${color}`}>{status}</span>
    </div>
  );
}
